/**
 * Onyx Hex material — typed class-name helpers + the shared clip-path geometry.
 *
 * The Onyx Hex design language gives every rectangular surface a soft-rounded
 * square with a single top-right hex notch, every status dot / loader / play
 * control a flat-top hexagon, and every clickable a raised keycap with a 3px
 * solid bottom edge. This module is the **one source** for the geometry strings
 * (consumed by `tailwind.preset.ts`'s `onyxMaterialPlugin`) and the typed class
 * names (so React consumers never hand-type `"nx-l"`).
 *
 * The runtime values here are plain data, frozen `as const` (no runtime cost),
 * matching `src/lib/motion.ts`'s convention. The CSS that these classes resolve
 * to ships two ways: inherited automatically from the Tailwind preset, or via
 * the opt-in `@aisquare/ui/onyx-hex.css` stylesheet (for non-preset consumers).
 *
 * Two load-bearing rules every consumer must obey (a `clip-path` surface breaks
 * two v1 assumptions):
 *   1. Elevation MUST be `filter: var(--dropPanel|--dropCard|--dropSoft)` (or the
 *      `elev.*` classes) — an outer `box-shadow` is CLIPPED AWAY by `clip-path`.
 *   2. Outlines / selection / focus MUST be drawn as an SVG STROKE OVERLAY (the
 *      `NxKeyline` primitive) — NOT a `border` (sliced at the notch diagonal) and
 *      NOT an inset `box-shadow` (clipped at the notch, feathered at the arcs).
 *      An absolutely-positioned, un-clipped `<svg>` sibling traces the EXACT
 *      notched silhouette (lines + arcs) so the line is continuous all the way
 *      around, including across the notch chamfer. See {@link nxSvgPath}.
 *
 * @example
 *   import { nx, hex, raise, elev, cn } from "@aisquare/ui";
 *   <button className={cn(nx.b, raise)}>Publish</button>
 *   <span className={cn("h-2 w-2 bg-success", hex)} />
 *   <section className={cn(nx.l, elev.panel)}>...</section>
 */

/** Class names for the five notch silhouettes, by size/role. */
export const nx = {
  /** panels — rail, Brain region, ConfigPanel shell, large surfaces. */
  l: "nx-l",
  /** cards — artifact tile, focus cards, hero, related, studio cards. */
  s: "nx-s",
  /** tiles / fields — icon tiles 36-44px, inputs, search, segmented track. */
  m: "nx-m",
  /** buttons — primary/secondary, breadcrumb back/list, upload, nav back. */
  b: "nx-b",
  /** chips — type chips, status pills, NEW/Draft chips, badges, the AikoMark container. */
  t: "nx-t",
} as const;

export type NxSize = keyof typeof nx;

/** Flat-top hexagon — status dots, bullets, step/check nodes, loader node, play control. */
export const hex = "hex" as const;

/** Raised keycap — a flat face over a 3px solid bottom edge; presses on `:active`. */
export const raise = "raise" as const;

/** Alias of {@link raise} (the spec + the handoff LOG both name it). */
export const key = "key" as const;

/** Clip-safe elevation classes (`filter: drop-shadow`), since `box-shadow` is clipped. */
export const elev = {
  /** panels — `--dropPanel`. */
  panel: "elev-panel",
  /** cards / tiles — `--dropCard`. */
  card: "elev-card",
  /** subtle — `--dropSoft`. */
  soft: "elev-soft",
} as const;

/**
 * Frosted-glass material class names for FLOATING surfaces (the `GlassSurface`
 * primitive composes these). A glass panel is TWO stacked, identically
 * notch-clipped layers because `backdrop-filter` and `filter` CANCEL on the
 * same node:
 *   - {@link glass.back} — the BACKING layer: `filter: var(--dropFloat)` (the
 *     float drop-shadow, which survives `clip-path` where a `box-shadow` is
 *     clipped away) over the `--glassBack` backstop fill. Empty, presentational.
 *   - {@link glass.panel} / {@link glass.composer} — the GLASS layer on top:
 *     `backdrop-filter: blur(22px) saturate(150%)` over the `--glass` translucent
 *     fill, the `--glassSheen` lumen, an inset rim keyline (`--glassBd` for
 *     `panel`, `--acLine` for the accent-lined `composer`) and a `--glassHi` top
 *     highlight. `children` render here.
 *
 * Floating glass panels do NOT take `.raise` (that is the base-button keycap);
 * the float here is `--dropFloat`, deeper than `--dropCard`.
 */
export const glass = {
  /** backing layer — float drop-shadow + backstop fill (no backdrop-filter). */
  back: "glass-back",
  /** glass layer — frost + sheen + `--glassBd` rim (the floating panel role). */
  panel: "glass-panel",
  /** glass layer — frost + sheen + `--acLine` rim (the accent-lined composer). */
  composer: "glass-composer",
} as const;

/** The two glass keyline roles (`panel` rim = `--glassBd`, `composer` rim = `--acLine`). */
export type GlassVariant = "panel" | "composer";

/* ----------------------------------------------------------------------------
 * Geometry — the tuned notch silhouette, as a SINGLE source.
 *
 * Every `nx-*` silhouette is the same shape parameterized by two numbers:
 *   - `r` — the corner radius of the three rounded corners (top-left, both
 *     bottom corners).
 *   - `n` — the size of the top-right hex notch chamfer (the diagonal cut).
 *
 * `NX_GEOMETRY` is the one place those pairs live. From it we generate (a) the
 * `clip-path: shape(...)` strings the preset/CSS apply, (b) the SVG `d` path the
 * `NxKeyline` overlay strokes (the only way to draw a CONTINUOUS keyline across
 * the notch — a clip slices a border/box-shadow), and (c) the `@supports`
 * fallback radius. Deriving all three from one table is what guarantees the
 * stroke registers EXACTLY on the clip silhouette (issue #158).
 *
 * Do NOT re-derive these by eye: the (r, n) values are tuned (Create Flow
 * L53-59). The CSS mirror in `src/tokens/onyx-hex.css` must stay byte-for-byte
 * in sync with the generated `NX_CLIP_PATH` strings.
 * ------------------------------------------------------------------------- */

/**
 * The tuned `(cornerRadius r, notch n)` pair for each size — the single source
 * the clip-path, the SVG keyline path, and the fallback radius all derive from.
 */
export const NX_GEOMETRY = {
  /** panels — rail, Brain region, large surfaces. */
  l: { r: 15, n: 20 },
  /** cards — artifact tile, focus cards, hero. */
  s: { r: 12, n: 15 },
  /** tiles / fields — icon tiles, inputs, search. */
  m: { r: 9, n: 12 },
  /** buttons — primary/secondary, nav back. */
  b: { r: 7, n: 10 },
  /** chips — type chips, status pills, badges. */
  t: { r: 5, n: 7 },
} as const;

/** Build one `clip-path: shape(...)` silhouette from a `(r, n)` pair. */
function nxClipPath({ r, n }: { r: number; n: number }): string {
  return `shape(from ${r}px 0, line to calc(100% - ${n}px) 0, line to 100% ${n}px, line to 100% calc(100% - ${r}px), arc to calc(100% - ${r}px) 100% of ${r}px cw, line to ${r}px 100%, arc to 0 calc(100% - ${r}px) of ${r}px cw, line to 0 ${r}px, arc to ${r}px 0 of ${r}px cw, close)`;
}

/**
 * The five `nx-*` `clip-path: shape(...)` silhouettes, generated from
 * {@link NX_GEOMETRY}. Output is byte-identical to the hand-tuned strings the
 * preset shipped (locked by `onyx-material.test.ts`).
 */
export const NX_CLIP_PATH = {
  l: nxClipPath(NX_GEOMETRY.l),
  s: nxClipPath(NX_GEOMETRY.s),
  m: nxClipPath(NX_GEOMETRY.m),
  b: nxClipPath(NX_GEOMETRY.b),
  t: nxClipPath(NX_GEOMETRY.t),
} as const;

/**
 * Build a `clip-path: shape(...)` RING (hollow frame) of width `w` px for a
 * `(r, n)` pair: the outer notched silhouette, then an inset inner one, with
 * `evenodd` so only the `w`-px frame between them paints. Pure `%`/`calc` (no
 * measurement) so it tracks a resizing box with ZERO lag and stays undistorted,
 * continuous across all three rounded corners AND the notch. This is the
 * continuous-border primitive — it replaces the measured `NxKeyline` SVG
 * overlay, whose path was computed for a stale width during a width animation
 * (the residue-triangle bug) and whose rounded corners a rectangular mask could
 * not follow. Apply as `clip-path` to a rim-colored layer (`.nx-rim`).
 */
function nxRingClip({ r, n }: { r: number; n: number }, w = 1): string {
  const ri = Math.max(0, r - w);
  return (
    "shape(evenodd " +
    `from ${r}px 0, line to calc(100% - ${n}px) 0, line to 100% ${n}px, line to 100% calc(100% - ${r}px), arc to calc(100% - ${r}px) 100% of ${r}px cw, line to ${r}px 100%, arc to 0 calc(100% - ${r}px) of ${r}px cw, line to 0 ${r}px, arc to ${r}px 0 of ${r}px cw, close, ` +
    `move to ${r}px ${w}px, line to calc(100% - ${n}px) ${w}px, line to calc(100% - ${w}px) ${n}px, line to calc(100% - ${w}px) calc(100% - ${r}px), arc to calc(100% - ${r}px) calc(100% - ${w}px) of ${ri}px cw, line to ${r}px calc(100% - ${w}px), arc to ${w}px calc(100% - ${r}px) of ${ri}px cw, line to ${w}px ${r}px, arc to ${r}px ${w}px of ${ri}px cw, close)`
  );
}

/** The five `nx-*` ring (1px frame) clip-paths, generated from {@link NX_GEOMETRY}. */
export const NX_RING_CLIP = {
  l: nxRingClip(NX_GEOMETRY.l),
  s: nxRingClip(NX_GEOMETRY.s),
  m: nxRingClip(NX_GEOMETRY.m),
  b: nxRingClip(NX_GEOMETRY.b),
  t: nxRingClip(NX_GEOMETRY.t),
} as const;

/**
 * Build the SVG `<path>` `d` that traces the EXACT notched silhouette of a
 * `w x h` box for a given size, for the `NxKeyline` stroke overlay.
 *
 * The path walks the same outline as {@link nxClipPath}: top edge from the
 * top-left corner radius to the start of the notch, the diagonal notch chamfer,
 * down the right edge, and around the two bottom corners + the left edge + the
 * top-left corner — three `A` (arc) commands for the three rounded corners; the
 * top-right is the notch (two `L` line segments, no arc).
 *
 * `inset` shifts every coordinate inward by that many px. An SVG stroke is
 * CENTERED on its path, so passing `inset = strokeWidth / 2` keeps the whole
 * line inside the box (its outer half would otherwise be clipped by the box
 * edge / `overflow`). Arc radii shrink by `inset` to stay concentric.
 *
 * @param size   notch size key
 * @param w      rendered box width in px (from `useMeasure`)
 * @param h      rendered box height in px
 * @param inset  inward offset (px); pass `strokeWidth / 2`. Default 0.
 */
export function nxSvgPath(size: NxSize, w: number, h: number, inset = 0): string {
  const { r: r0, n: n0 } = NX_GEOMETRY[size];
  // Clamp so degenerate (tiny) boxes never produce negative / crossed coords.
  const r = Math.max(0, Math.min(r0 - inset, (w - 2 * inset) / 2, (h - 2 * inset) / 2));
  const n = Math.max(0, Math.min(n0 - inset, w - 2 * inset - r));
  const x0 = inset;
  const y0 = inset;
  const x1 = w - inset;
  const y1 = h - inset;
  return [
    `M ${x0 + r} ${y0}`, // start after the top-left corner radius, on the top edge
    `L ${x1 - n} ${y0}`, // top edge up to the notch
    `L ${x1} ${y0 + n}`, // notch chamfer (the diagonal cut) — 2 lines, no arc
    `L ${x1} ${y1 - r}`, // down the right edge to the bottom-right corner
    `A ${r} ${r} 0 0 1 ${x1 - r} ${y1}`, // bottom-right rounded corner
    `L ${x0 + r} ${y1}`, // bottom edge
    `A ${r} ${r} 0 0 1 ${x0} ${y1 - r}`, // bottom-left rounded corner
    `L ${x0} ${y0 + r}`, // up the left edge
    `A ${r} ${r} 0 0 1 ${x0 + r} ${y0}`, // top-left rounded corner
    "Z",
  ].join(" ");
}

/**
 * Flat-top hexagon clip — status dots, loader node, the play control.
 *
 * A regular hexagon is FLAT-TOP in a `1 : 0.866` box, so on the SQUARE (1:1)
 * containers used for dots/play controls it reads tight and correct. (A
 * pointy-top hex is regular only in a `1 : 1.1547` box; forcing it into a square
 * stretched it ~13% too wide — the "ugly and wide" report in #158.) Flat-top is
 * the standard premium choice for square status dots. `AikoMark`'s container is
 * a rounded square (CSS `border-radius`), NOT this clip, so the flip is local to
 * the dot/loader/play role.
 */
export const HEX_CLIP_PATH = "polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%)" as const;

/**
 * Glass material geometry — theme-agnostic, lifted verbatim from the design
 * file's live glass node. Imported by the preset plugin so the recipe lives in
 * exactly one place; only the 7 glass token VALUES flip under `.dark`.
 */
export const GLASS_BLUR_PX = 22 as const;
/** `backdrop-filter` value at the canonical 22px blur (the `GlassSurface` default). */
export const GLASS_BACKDROP_FILTER = "blur(22px) saturate(150%)" as const;
/** The blue-lumen sheen, a top-right radial (origin + falloff verbatim from the design node). */
export const GLASS_SHEEN_GRADIENT =
  "radial-gradient(circle at 82% -8%, var(--glassSheen), transparent 58%)" as const;

/** `@supports`-fallback radius per notch size (degrades to a rounded-rect, no notch). */
export const NX_FALLBACK_RADIUS = {
  l: `${NX_GEOMETRY.l.r}px`,
  s: `${NX_GEOMETRY.s.r}px`,
  m: `${NX_GEOMETRY.m.r}px`,
  b: `${NX_GEOMETRY.b.r}px`,
  t: `${NX_GEOMETRY.t.r}px`,
} as const;
